import React, { useCallback, useEffect, useState } from "react";

import { useProcessCoADocument } from "@api/fileScanning.api";
import { pluralize } from "@common/helpers/string";
import {
    AccountClass,
    AccountSubtype,
    AccountType,
} from "@common/constants/domains/accounting/accounts";
import { toBase64 } from "@helpers/file";
import { uploadDocument } from "@lib/financialDocument";
import { useScope } from "@state/scopeContext.lib";
import { useAccounts } from "@hooks/useAccounts";
import { StandardModal } from "@design-system/Modal/Modal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useAccountingTabContext } from "../../useAccountingTab";
import { CreateAccountSpreadsheetModalContent } from "./CreateAccountSpreadsheetModalContent";
import { useAccountCreation } from "./useAccountCreationMutation";
import { validateAndTransformAccounts } from "./validateAndTransformAccounts";

export interface AccountProxyObject {
    id: string;
    name: string;
    accountType: AccountType | null;
    accountSubtype: AccountSubtype | null;
    mappedKickAccountId: string | null;
    parentGroupId: string | null;
}

interface Props {
    show: boolean;
    onHide: () => void;
    availableClassifications?: AccountClass[];
}

const DEFAULT_NUMBER_OF_LINES = 10;

function buildEmptyLines(
    size: number = DEFAULT_NUMBER_OF_LINES,
): AccountProxyObject[] {
    return new Array(size).fill(null).map(() => ({
        id: Math.random().toString(),
        name: "",
        accountType: null,
        accountSubtype: null,
        mappedKickAccountId: null,
        parentGroupId: null,
    }));
}

export interface ValidationErrors {
    accounts?: Array<{
        name?: string;
        accountType?: string;
        mappedKickAccountId?: string;
    }>;
    noAccounts?: string;
}

export const CreateAccountSpreadsheetModal: React.FC<Props> = React.memo(
    ({ show, onHide, availableClassifications }) => {
        const [loading, setLoading] = useState(false);
        const { toast } = useToaster();
        const { selectedEntity } = useAccountingTabContext();
        const { workspaceId } = useScope();
        const [isUploadLoading, setIsUploadLoading] = useState(false);
        const [accounts, setAccounts] =
            useState<AccountProxyObject[]>(buildEmptyLines());
        const [validationErrors, setValidationErrors] =
            useState<ValidationErrors>({});
        const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

        const save = useAccountCreation();
        const processCoADocument = useProcessCoADocument();
        const { accounts: existingAccounts } = useAccounts({
            entityId: selectedEntity?.id,
        });

        const isFilledAccount = useCallback(
            ({ name, accountType }: AccountProxyObject): boolean =>
                !!name.trim() && !!accountType,
            [],
        );

        const validateAccounts = useCallback((): ValidationErrors => {
            const hasAtLeastOneFilledAccount = accounts.some((account) =>
                isFilledAccount(account),
            );

            if (!hasAtLeastOneFilledAccount) {
                return { noAccounts: "Add at least one account" };
            }

            const accountErrors = accounts.map((account) => {
                if (isFilledAccount(account)) {
                    return {};
                }

                if (
                    !account.name &&
                    !account.accountType &&
                    !account.accountSubtype
                ) {
                    return {};
                }

                return {
                    name: !account.name.trim() ? "Missing name" : undefined,
                    accountType: !account.accountType
                        ? "Missing account type"
                        : undefined,
                };
            });

            const hasAccountError = accountErrors.some(
                (error) => error.name ?? error.accountType,
            );

            if (hasAccountError) {
                return { accounts: accountErrors };
            }

            return {};
        }, [accounts, isFilledAccount]);

        useEffect(() => {
            if (hasAttemptedSubmit) {
                setValidationErrors(validateAccounts());
            }
        }, [accounts, hasAttemptedSubmit, validateAccounts]);

        const handleSave = async () => {
            setHasAttemptedSubmit(true);
            const errors = validateAccounts();
            setValidationErrors(errors);

            if (Object.keys(errors).length > 0) {
                return;
            }

            const accountsToCreate = accounts
                .filter(
                    (account) =>
                        isFilledAccount(account) &&
                        !account.mappedKickAccountId,
                )
                .map((account) => ({
                    name: account.name.trim(),
                    type: account.accountType!,
                    parentGroupId: account.parentGroupId,
                }));

            setLoading(true);
            try {
                const result = await save.mutateAsync({
                    entityId: selectedEntity!.id,
                    accounts: accountsToCreate,
                });

                if (result) {
                    toast(
                        `Created ${accountsToCreate.length} ${pluralize("account", accountsToCreate.length)} for ${selectedEntity!.name}`,
                    );
                }

                setAccounts(buildEmptyLines());
                setValidationErrors({});
                setHasAttemptedSubmit(false);
                onHide();
            } finally {
                setLoading(false);
            }
        };

        const handleHide = () => {
            setAccounts(buildEmptyLines());
            setValidationErrors({});
            setHasAttemptedSubmit(false);
            onHide();
        };

        const findMatchingAccountId = (accountName: string): string | null => {
            const matchedAccount = existingAccounts.find(
                (account) =>
                    account.name.toLowerCase() ===
                    accountName.toLowerCase().trim(),
            );
            return matchedAccount?.id ?? null;
        };

        const handleFileUpload = async (
            event: React.ChangeEvent<HTMLInputElement>,
        ) => {
            const file = event.target.files?.[0];
            if (!file) {
                return;
            }

            if (!workspaceId) {
                throw new Error("No active workspace");
            }

            if (!selectedEntity) {
                toast("Select an entity first");
                return;
            }

            setIsUploadLoading(true);
            try {
                const financialDocument = await uploadDocument({
                    workspaceId,
                    file: await toBase64(file),
                    filename: file.name,
                    contentType: file.type,
                });

                const result = await processCoADocument.mutateAsync({
                    entityId: selectedEntity.id,
                    documentId: financialDocument.id,
                });

                if (!result.success || !result.accountMappings) {
                    throw new Error("Invalid response format");
                }

                const newAccounts: AccountProxyObject[] =
                    result.accountMappings.accountMappings.map((mapping) => ({
                        id: Math.random().toString(),
                        name: mapping.originalAccountName,
                        accountType: mapping.mappedAccountType ?? null,
                        accountSubtype: mapping.mappedAccountSubtype ?? null,
                        mappedKickAccountId: mapping.mappedAccountName
                            ? findMatchingAccountId(mapping.mappedAccountName)
                            : null,
                        parentGroupId: null,
                    }));

                const accountsWithEmptyLines = [
                    ...newAccounts,
                    ...buildEmptyLines(3),
                ];

                // Apply validation and transformation logic to AI-generated accounts
                const validatedAccounts = validateAndTransformAccounts({
                    newData: accountsWithEmptyLines,
                    prevData: [],
                    existingAccounts,
                });

                setAccounts(validatedAccounts);
            } catch (uploadError) {
                toast(
                    `Failed to upload file: ${uploadError instanceof Error ? uploadError.message : "Please try again."}`,
                );
            } finally {
                setIsUploadLoading(false);
                event.target.value = "";
            }
        };

        return (
            <StandardModal
                size="xl"
                onHide={handleHide}
                show={show}
                disableAnimation
            >
                <CreateAccountSpreadsheetModalContent
                    loading={loading}
                    availableClassifications={availableClassifications}
                    accounts={accounts}
                    onAccountsChange={setAccounts}
                    onSave={handleSave}
                    validationErrors={validationErrors}
                    hasAttemptedSubmit={hasAttemptedSubmit}
                    onFileUpload={handleFileUpload}
                    isUploadLoading={isUploadLoading}
                />
            </StandardModal>
        );
    },
);
